import { useCallback, useEffect, useRef, useState } from "react";
import { scheduleBlocks, type StaffScheduleBlock } from "./scheduleBlocks.js";

type Result = { blocks: StaffScheduleBlock[]; error: string | null };

/** Provider reads are independent of grid columns, bookings and rota. */
export function useDiaryBlocks(
  providerIds: readonly number[],
  from: string,
  to: string,
  enabled: boolean,
  scope: string,
) {
  const selectedProviderIds = [...new Set(providerIds)].sort((a, b) => a - b);
  const providerKey = selectedProviderIds.join(",");
  const context = `${scope}|${enabled}|${from}|${to}`;
  const [store, setStore] = useState(() => ({ context, results: new Map<number, Result>() }));
  // Reset during render so even the first paint of a new window has no old
  // shading. Object identity also rejects an old A response after A → B → A.
  const requests = useRef({ tokens: new Map<number, object>() });
  if (store.context !== context) {
    setStore({ context, results: new Map() });
    requests.current = { tokens: new Map() };
  }
  const current = useRef({ enabled, providerKey });
  current.current = { enabled, providerKey };

  useEffect(() => () => { requests.current.tokens.clear(); }, []);

  useEffect(() => {
    if (!enabled || store.context !== context) return;
    const batch = requests.current;
    for (const providerId of providerKey.split(",").filter(Boolean).map(Number)) {
      if (store.results.has(providerId) || batch.tokens.has(providerId)) continue;
      const token = {};
      batch.tokens.set(providerId, token);
      const save = (result: Result) => {
        if (requests.current !== batch || batch.tokens.get(providerId) !== token) return;
        setStore((previous) => {
          if (requests.current !== batch || batch.tokens.get(providerId) !== token) return previous;
          const results = new Map(previous.results);
          results.set(providerId, result);
          return { context, results };
        });
      };
      void scheduleBlocks.list(providerId, from, to).then(
        (blocks) => save({ blocks, error: null }),
        (cause: unknown) => save({
          blocks: [],
          error: cause instanceof Error && cause.message ? cause.message : "Blocked time could not be loaded.",
        }),
      );
    }
  }, [context, enabled, from, providerKey, store, to]);

  // Mutation callbacks can settle after navigation. Always invalidate the
  // current window, and supersede any pre-mutation request for that provider.
  const reload = useCallback((providerId?: number) => {
    if (!current.current.enabled) return;
    const ids = providerId === undefined
      ? current.current.providerKey.split(",").filter(Boolean).map(Number)
      : [providerId];
    for (const id of ids) requests.current.tokens.delete(id);
    setStore((previous) => {
      const results = new Map(previous.results);
      for (const id of ids) results.delete(id);
      return { ...previous, results };
    });
  }, []);

  const results = store.context === context && enabled ? store.results : new Map<number, Result>();
  const selected = selectedProviderIds.map((id) => results.get(id));
  return {
    blocks: selected.flatMap((result) => result?.blocks ?? []),
    error: selected.find((result) => result?.error)?.error ?? null,
    reload,
  };
}
